/**
 * Article Table of Contents
 * - Scans .article-content for h2 (and optionally h3) headings
 * - Auto-injects anchor IDs on headings
 * - Builds a nested <ol> TOC inside [data-toc]
 * - Highlights the active heading via scroll-spy (IntersectionObserver)
 * - Hides the entire TOC block if fewer headings than min_headings threshold
 */

(function () {
  'use strict';

  function slugify(text) {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function buildToc(tocEl) {
    const articleContent = document.querySelector('.article-content.halo-text-format');
    if (!articleContent) return;

    const includeH3 = tocEl.dataset.includeH3 === 'true';
    const minHeadings = parseInt(tocEl.dataset.minHeadings, 10) || 2;
    const selector = includeH3 ? 'h2, h3' : 'h2';

    const headings = Array.from(articleContent.querySelectorAll(selector));

    // Hide TOC if not enough headings
    if (headings.length < minHeadings) {
      const sidebarBlock = tocEl.closest('.sidebarBlock--toc');
      if (sidebarBlock) sidebarBlock.style.display = 'none';
      return;
    }

    // Assign IDs to headings that don't have one
    headings.forEach(function (heading, index) {
      if (!heading.id) {
        const text = heading.textContent || '';
        const base = slugify(text) || 'heading';
        let id = base;
        // Ensure uniqueness
        let count = 0;
        while (document.getElementById(id)) {
          count++;
          id = base + '-' + count;
        }
        heading.id = id;
      }
    });

    // Build nested list
    const ol = document.createElement('ol');
    ol.className = 'article-toc__list';

    let currentH2Li = null;
    let currentH3Ol = null;

    headings.forEach(function (heading) {
      const li = document.createElement('li');
      li.className = 'article-toc__item article-toc__item--' + heading.tagName.toLowerCase();

      const a = document.createElement('a');
      a.href = '#' + heading.id;
      a.className = 'article-toc__link';
      a.textContent = heading.textContent.trim();
      a.dataset.tocTarget = heading.id;

      // Smooth scroll
      a.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.getElementById(heading.id);
        if (target) {
          const offset = 80; // account for sticky headers
          const top = target.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top: top, behavior: 'smooth' });
          // Update URL hash without jumping
          history.pushState(null, null, '#' + heading.id);
        }
      });

      li.appendChild(a);

      if (heading.tagName === 'H2') {
        currentH2Li = li;
        currentH3Ol = null;
        ol.appendChild(li);
      } else if (heading.tagName === 'H3' && includeH3) {
        if (!currentH2Li) {
          // Orphan h3: append directly
          ol.appendChild(li);
        } else {
          if (!currentH3Ol) {
            currentH3Ol = document.createElement('ol');
            currentH3Ol.className = 'article-toc__sublist';
            currentH2Li.appendChild(currentH3Ol);
          }
          currentH3Ol.appendChild(li);
        }
      }
    });

    // Remove placeholder empty message and insert list
    const emptyMsg = tocEl.querySelector('.article-toc__empty');
    if (emptyMsg) emptyMsg.remove();
    tocEl.appendChild(ol);

    // Scroll-spy via IntersectionObserver
    initScrollSpy(headings, tocEl);
  }

  function initScrollSpy(headings, tocEl) {
    if (!('IntersectionObserver' in window)) return;

    const allLinks = tocEl.querySelectorAll('.article-toc__link');

    function setActive(id) {
      allLinks.forEach(function (link) {
        const isActive = link.dataset.tocTarget === id;
        link.classList.toggle('is-active', isActive);
        // Also mark parent li
        link.parentElement.classList.toggle('is-active', isActive);
      });
    }

    // Track which headings are visible
    const visibleSet = new Set();

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            visibleSet.add(entry.target.id);
          } else {
            visibleSet.delete(entry.target.id);
          }
        });

        // Highlight the first currently visible heading
        // Fall back to the last one that scrolled past
        if (visibleSet.size > 0) {
          const firstVisible = headings.find(function (h) {
            return visibleSet.has(h.id);
          });
          if (firstVisible) setActive(firstVisible.id);
        } else {
          // Find the last heading above the viewport
          const scrollTop = window.scrollY;
          let lastAbove = null;
          headings.forEach(function (h) {
            const rect = h.getBoundingClientRect();
            if (rect.top + window.scrollY <= scrollTop + 120) {
              lastAbove = h;
            }
          });
          if (lastAbove) setActive(lastAbove.id);
        }
      },
      {
        rootMargin: '-80px 0px -60% 0px',
        threshold: 0,
      }
    );

    headings.forEach(function (h) {
      observer.observe(h);
    });
  }

  function init() {
    const tocEls = document.querySelectorAll('[data-toc]');
    tocEls.forEach(function (tocEl) {
      buildToc(tocEl);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();